import "@randajan/jet-core";
import { ymd } from "../tools/dateStrings.js";
import { readLocaleCandidate } from "../locale/tools.js";

import { fail, hasValue, ok, toDate, toText } from "./shared.js";
import {
    parseBoolean,
    runValidators,
    validateBooleanParsed,
    validateChars,
    validateHasPattern,
    validateLength,
    validateMatches,
    validateMaxLength,
    validateNotFutureDate,
    validateRecurring
} from "./validators.js";

const emailRegex = RegExp.jet.lib.email;
const noControlCharsRegex = /^[^\p{C}]*$/u;
const nameCharsRegex = /^[\p{L}\p{M}][\p{L}\p{M}\s.'-]*$/u;
const placeCharsRegex = /^[\p{L}\p{M}][\p{L}\p{M}\s.'-]*$/u;
const streetCharsRegex = /^[\p{L}\p{M}\p{N}][\p{L}\p{M}\p{N}\s.',\/-]*$/u;
const houseCharsRegex = /^[\p{L}\p{M}\p{N}][\p{L}\p{M}\p{N}\s\/-]*$/u;
const aliasCharsRegex = /^[\p{Script=Latin}\p{M}\p{N}._ -]+$/u;
const aliasHasLetterRegex = /\p{Script=Latin}/u;
const phoneRegex = /^\+?[0-9][0-9().\-\s]{5,22}[0-9]$/;
const pinRegex = /^[0-9]+$/;
const zipRegex = /^[\p{L}\p{N}][\p{L}\p{N}\s-]{2,15}$/u;

const invalidDate = Object.freeze({ value:null, isEmpty:false, isValid:false });

const finish = (value, error)=>error ? fail(value, error) : ok(value);

const toYmdDate = raw=>{
    if (!hasValue(raw)) { return { value:null, isEmpty:true, isValid:true }; }
    if (typeof raw !== "string") { return invalidDate; }

    const text = raw.trim();
    const date = ymd.from(text);
    if (!(date instanceof Date) || !Number.isFinite(date.getTime())) { return invalidDate; }
    if (ymd.to(date) !== text) { return invalidDate; }
    return { value:date, isEmpty:false, isValid:true };
};

const validateTextField = (raw, options, rules)=>{
    const value = toText(raw, options);
    if (!value) { return ok(value); }
    return finish(value, runValidators(value, rules));
};

const noControlRule = value=>validateChars(value, noControlCharsRegex, { chars:"no_control" });

const nameRules = Object.freeze([
    value=>validateLength(value, { min:2, max:64 }),
    noControlRule,
    value=>validateChars(value, nameCharsRegex, { chars:"name" })
]);

const placeRules = max=>Object.freeze([
    value=>validateLength(value, { min:2, max }),
    noControlRule,
    value=>validateChars(value, placeCharsRegex, { chars:"place" })
]);

const streetRules = Object.freeze([
    value=>validateLength(value, { min:2, max:120 }),
    noControlRule,
    value=>validateChars(value, streetCharsRegex, { chars:"street" })
]);

const houseRules = Object.freeze([
    value=>validateMaxLength(value, 24),
    noControlRule,
    value=>validateChars(value, houseCharsRegex, { chars:"house" })
]);

const aliasRules = Object.freeze([
    value=>validateLength(value, { min:3, max:16 }),
    value=>validateHasPattern(value, aliasHasLetterRegex, { pattern:"latin_letter" }),
    value=>validateChars(value, aliasCharsRegex, { chars:"alias" }),
    validateRecurring
]);

const pinRules = Object.freeze([
    value=>validateLength(value, { min:4, max:8 }),
    value=>validateMatches(value, pinRegex, { format:"digits" })
]);

const locationRules = placeRules(80);
const phoneRules = Object.freeze([
    value=>validateMaxLength(value, 24),
    noControlRule,
    value=>validateMatches(value, phoneRegex, { format:"phone" })
]);
const zipRules = Object.freeze([
    value=>validateMaxLength(value, 16),
    noControlRule,
    value=>validateMatches(value, zipRegex, { format:"zip" })
]);
export const validateName = raw=>validateTextField(raw, { max:128 }, nameRules);
export const validateLocale = raw=>{
    const { value, error, details } = readLocaleCandidate(raw);
    if (!error) { return ok(value); }
    return fail(value, error, details);
};

export const validateEmail = raw=>{
    const value = toText(raw, { max:256, lower:true });
    if (!value) { return ok(value); }
    const error = runValidators(value, [
        rawValue=>validateMaxLength(rawValue, 120),
        rawValue=>validateMatches(rawValue, emailRegex, { format:"email" })
    ]);
    return finish(value, error);
};
export const validatePhone = raw=>validateTextField(raw, { max:48 }, phoneRules);
export const validateCountry = raw=>validateTextField(raw, { max:128 }, locationRules);
export const validateCity = raw=>validateTextField(raw, { max:128 }, locationRules);
export const validateStreet = raw=>validateTextField(raw, { max:160 }, streetRules);
export const validateHouse = raw=>validateTextField(raw, { max:48 }, houseRules);
export const validateZip = raw=>validateTextField(raw, { max:32 }, zipRules);
export const validateBirthday = raw=>{
    const parsed = toYmdDate(raw);
    if (parsed.isEmpty) { return ok(null); }
    if (!parsed.isValid) { return fail(null, "invalid_format", { format:"date" }); }
    const error = validateNotFutureDate(parsed.value);
    if (error) { return fail(null, error); }
    return ok(ymd.to(parsed.value));
};
export const validateMailMeMuteTill = raw=>{
    const parsed = toDate(raw);
    if (parsed.isEmpty) { return ok(null); }
    if (!parsed.isValid) { return fail(null, "invalid_format", { format:"date" }); }
    return ok(parsed.value);
};
const validateBooleanField = raw=>{
    const parsed = parseBoolean(raw, false);
    return finish(parsed.value, validateBooleanParsed(parsed));
};

export const validateIsPublic = validateBooleanField;

export const validateAlias = raw=>{
    const value = toText(raw, { max:64 });
    if (!value) { return ok(value); }
    return finish(value, runValidators(value, aliasRules));
};

export const validatePin = raw=>validateTextField(raw, { max:16 }, pinRules);
export const validatePinConfirm = raw=>validatePin(raw);
export const validateMailMeBackoffice = validateBooleanField;
export const validateMailMeGameNews = validateBooleanField;
export const validateMailMeMarketing = validateBooleanField;
export const validateMailMeBackofficeMuteTill = validateMailMeMuteTill;
export const validateMailMeGameNewsMuteTill = validateMailMeMuteTill;
export const validateMailMeMarketingMuteTill = validateMailMeMuteTill;
export const validateConfirmDelete = validateBooleanField;
export const validateDeletePin = raw=>validatePin(raw);

export const profileFieldValidators = Object.freeze({
    name: validateName,
    locale: validateLocale,
    email: validateEmail,
    phone: validatePhone,
    country: validateCountry,
    city: validateCity,
    street: validateStreet,
    house: validateHouse,
    zip: validateZip,
    birthday: validateBirthday,
    isPublic: validateIsPublic,
    alias: validateAlias,
    pin: validatePin,
    pinConfirm: validatePinConfirm,
    mailMeBackoffice: validateMailMeBackoffice,
    mailMeGameNews: validateMailMeGameNews,
    mailMeMarketing: validateMailMeMarketing,
    mailMeBackofficeMuteTill: validateMailMeBackofficeMuteTill,
    mailMeGameNewsMuteTill: validateMailMeGameNewsMuteTill,
    mailMeMarketingMuteTill: validateMailMeMarketingMuteTill,
    confirmDelete: validateConfirmDelete,
    deletePin: validateDeletePin
});
